import { useState } from 'react'
import { creditsApi } from '../api/credits'
import type { Plan, TenantUser } from '../api/types'
import { usersApi } from '../api/users'
import { can } from '../auth/AuthContext'
import { useAuth } from '../auth/useAuth'
import { BbIcon } from '../components/BbIcon'
import { ErrorState, LoadingState } from '../components/States'
import { useCompanies } from '../companies/useCompanies'
import { useAsync } from '../hooks/useAsync'
import { formatInt } from '../lib/format'
import { BillingPage } from './BillingPage'
import { CompaniesPage } from './CompaniesPage'
import { UsersPage } from './UsersPage'

/* Ported from "batchbooks_account_summary". The hub only draws the KPI strip and
   the tab bar; each tab hands off to the page that already owns that surface,
   rendered with `embedded` so it drops its own title and page padding.

   The prototype's fourth KPI is a next-billing date. Nothing in the contract
   carries one — Plan has id/name/priceUsd/monthlyCredits/maxCompanies/maxUsers,
   CreditsResponse carries a balance and a ledger — so that card shows an
   em-dash rather than a number invented on the client.

   The contract also has a single billing surface, so "Manage Subscription" and
   "Billing Details" are the two halves of BillingPage: the plan/allowance/top-up
   cards plus the buy-credits flow, and the credit ledger with its pager. */

const DASH = '—'

type TabId = 'users' | 'subscription' | 'companies' | 'billing'

interface Tab {
  id: TabId
  label: string
}

export function AccountPage() {
  const { user } = useAuth()
  const isAdmin = can(user?.role, 'admin')
  const { companies, loading: companiesLoading, error: companiesError, reload: reloadCompanies } = useCompanies()

  // GET /users is admin-only today, so a narrow role never issues the call.
  const users = useAsync<TenantUser[] | null>(
    () => (isAdmin ? usersApi.list() : Promise.resolve(null)),
    [isAdmin],
  )
  const plans = useAsync<Plan[]>(() => creditsApi.plans(), [])

  const tabs: Tab[] = [
    ...(isAdmin ? [{ id: 'users' as const, label: 'User Details' }] : []),
    { id: 'subscription', label: 'Manage Subscription' },
    { id: 'companies', label: 'Company Details' },
    { id: 'billing', label: 'Billing Details' },
  ]
  const [wanted, setWanted] = useState<TabId>('users')
  const active = tabs.some((tab) => tab.id === wanted) ? wanted : tabs[0].id

  const currentPlan = plans.data?.find((plan) => plan.isCurrent) ?? null
  const userCount = users.data?.length ?? null
  const companyCount = companies.length
  const usersLeft = currentPlan && userCount !== null ? Math.max(0, currentPlan.maxUsers - userCount) : null
  const companiesLeft = currentPlan ? Math.max(0, currentPlan.maxCompanies - companyCount) : null

  const loadError = plans.error ?? users.error ?? companiesError
  const loading =
    (plans.loading && !plans.data) ||
    (isAdmin && users.loading && !users.data) ||
    (companiesLoading && companies.length === 0)

  const retry = () => {
    plans.reload()
    users.reload()
    reloadCompanies()
  }

  return (
    <div className="bb bb-page bb-page-wide">
      <h1>Account</h1>

      {loadError ? (
        <ErrorState error={loadError} onRetry={retry} />
      ) : loading ? (
        <LoadingState label="Loading your account…" />
      ) : (
        <div className="bb-kpis">
          <div className="bb-kpi">
            <div className="bb-kpi-h">
              <span className="bb-kpi-ico">
                <BbIcon name="users" />
              </span>
              <span className="bb-kpi-t">Total Users</span>
            </div>
            <div className="bb-kpi-b">
              <span className="bb-kpi-n">{userCount === null ? DASH : formatInt(userCount)}</span>
              <span className="bb-kpi-m">
                Used: {userCount === null ? DASH : formatInt(userCount)}
                <b>Available: {usersLeft === null ? DASH : formatInt(usersLeft)}</b>
              </span>
            </div>
          </div>

          <div className="bb-kpi">
            <div className="bb-kpi-h">
              <span className="bb-kpi-ico">
                <BbIcon name="building" />
              </span>
              <span className="bb-kpi-t">Total Companies</span>
            </div>
            <div className="bb-kpi-b">
              <span className="bb-kpi-n">{formatInt(companyCount)}</span>
              <span className="bb-kpi-m">
                Used: {formatInt(companyCount)}
                <b>Available: {companiesLeft === null ? DASH : formatInt(companiesLeft)}</b>
              </span>
            </div>
          </div>

          <div className="bb-kpi">
            <div className="bb-kpi-h">
              <span className="bb-kpi-ico">
                <BbIcon name="file-text" />
              </span>
              <span className="bb-kpi-t">Subscription</span>
            </div>
            <div className="bb-kpi-b">
              <span className="bb-kpi-m">
                Plan
                <b>{currentPlan?.name ?? user?.planName ?? DASH}</b>
              </span>
              <span className="bb-kpi-m">
                Status
                <b>{currentPlan ? 'Active' : DASH}</b>
              </span>
            </div>
          </div>

          <div className="bb-kpi">
            <div className="bb-kpi-h">
              <span className="bb-kpi-ico">
                <BbIcon name="calendar" />
              </span>
              <span className="bb-kpi-t">Next Billing</span>
            </div>
            <div className="bb-kpi-b">
              <span className="bb-kpi-m">
                Date
                <b>{DASH}</b>
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bb-activity">
        <div className="bb-subtabs" role="tablist">
          {tabs.map((tab) => (
            <button
              className={tab.id === active ? 'bb-subtab bb-subtab-on' : 'bb-subtab'}
              key={tab.id}
              id={`account-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={tab.id === active}
              aria-controls="account-tabpanel"
              onClick={() => setWanted(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          className="bb-subtab-b"
          id="account-tabpanel"
          role="tabpanel"
          aria-labelledby={`account-tab-${active}`}
        >
          {active === 'users' ? <UsersPage embedded /> : null}
          {active === 'subscription' ? <BillingPage embedded section="plan" /> : null}
          {active === 'companies' ? <CompaniesPage embedded /> : null}
          {active === 'billing' ? <BillingPage embedded section="ledger" /> : null}
        </div>
      </div>
    </div>
  )
}
